import React, { useState, useRef } from 'react'
import i18n from '../../../../../../../lib/i18n'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, Tabs, Input, Button, Alert, Spin } from 'antd'
import { FaVideo, FaUpload, FaLink, FaFolderOpen } from 'react-icons/fa'
import { addSlideVideoFile, addSlideVideoLink } from '../../../../../actions'

const limitCount = 150;

function UploadSliderVideo({ countImgs = 0 }) {
  const dispatch = useDispatch()
  const slider = useSelector(state => state.slider.slider)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('1')
  const [videoFile, setVideoFile] = useState(null)
  const [videoUrl, setVideoUrl] = useState('')
  const [uploading, setUploading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const fileInputRef = useRef(null)

  const handleOpen = () => {
    setIsModalOpen(true)
    setErrorMsg('')
    setVideoFile(null)
    setVideoUrl('')
  }

  const handleClose = () => {
    if (uploading) return
    setIsModalOpen(false)
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!file.type.startsWith('video/')) {
      setErrorMsg(i18n('sl_video_type_error'))
      return
    }

    setErrorMsg('')
    setVideoFile(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (!file) return

    if (!file.type.startsWith('video/')) {
      setErrorMsg(i18n('sl_video_type_error'))
      return
    }

    setErrorMsg('')
    setVideoFile(file)
  }

  const handleUploadFile = async () => {
    if (!videoFile) return
    if (countImgs >= limitCount) {
      setErrorMsg(i18n('sl_slide_img_msg_count'))
      return
    }

    try {
      setUploading(true)
      setErrorMsg('')
      await dispatch(addSlideVideoFile(videoFile, slider.id))
      setUploading(false)
      setIsModalOpen(false)
    } catch (err) {
      setUploading(false)
      setErrorMsg(i18n('sl_video_upload_error'))
    }
  }

  const handleAddLink = async () => {
    if (!videoUrl.trim()) return
    if (countImgs >= limitCount) {
      setErrorMsg(i18n('sl_slide_img_msg_count'))
      return
    }

    const isYoutube = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');
    const isVimeo = videoUrl.includes('vimeo.com');
    const isDirect = videoUrl.match(/\.(mp4|webm|ogg|mov|avi|mkv)/i);

    if (!isYoutube && !isVimeo && !isDirect) {
      setErrorMsg(i18n('sl_video_url_error'))
      return
    }

    try {
      setUploading(true)
      setErrorMsg('')
      await dispatch(addSlideVideoLink(videoUrl.trim(), slider.id))
      setUploading(false)
      setIsModalOpen(false)
    } catch (err) {
      setUploading(false)
      setErrorMsg(i18n('sl_video_upload_error'))
    }
  }

  const tabItems = [
    {
      key: '1',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FaUpload /> {i18n('sl_upload_video_file')}
        </span>
      ),
      children: (
        <div style={{ marginTop: '16px' }}>
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: '2px dashed #00adff',
              borderRadius: '8px',
              padding: '40px 20px',
              textAlign: 'center',
              backgroundColor: '#f0f9ff',
              cursor: 'pointer',
              transition: 'border-color 0.3s ease',
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              accept="video/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <FaFolderOpen size={48} color="#00adff" style={{ marginBottom: '12px' }} />
            <p style={{ fontSize: '16px', fontWeight: '500', color: '#333', marginBottom: '4px' }}>
              {i18n('sl_drag_video_here')}
            </p>
            <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
              {i18n('sl_video_format_hint')}
            </p>
          </div>

          {videoFile && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              borderRadius: '6px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontWeight: '500', color: '#495057', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }}>
                {videoFile.name}
              </span>
              <span style={{ fontSize: '12px', color: '#6c757d' }}>
                {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
              </span>
            </div>
          )}

          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button onClick={handleClose} disabled={uploading}>
              {i18n('Cancel')}
            </Button>
            <Button
              type="primary"
              onClick={handleUploadFile}
              disabled={!videoFile || uploading}
              style={{ backgroundColor: '#00adff', borderColor: '#00adff' }}
            >
              {uploading ? <Spin size="small" /> : i18n('Upload')}
            </Button>
          </div>
        </div>
      )
    },
    {
      key: '2',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FaLink /> {i18n('sl_embed_video_link')}
        </span>
      ),
      children: (
        <div style={{ marginTop: '16px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
              {i18n('sl_video_url_label')}
            </label>
            <Input
              placeholder="https://www.youtube.com/watch?v=..."
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              disabled={uploading}
              size="large"
            />
          </div>

          <p style={{ fontSize: '12px', color: '#666', lineHeight: '1.5' }}>
            {i18n('sl_supported_services')}
          </p>

          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button onClick={handleClose} disabled={uploading}>
              {i18n('Cancel')}
            </Button>
            <Button
              type="primary"
              onClick={handleAddLink}
              disabled={!videoUrl.trim() || uploading}
              style={{ backgroundColor: '#00adff', borderColor: '#00adff' }}
            >
              {uploading ? <Spin size="small" /> : i18n('Add')}
            </Button>
          </div>
        </div>
      )
    }
  ]

  return (
    <>
      <span onClick={handleOpen} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
        {i18n('sl_add_slide_video')}
      </span>

      <Modal
        title={i18n('sl_video_modal_title')}
        open={isModalOpen}
        onCancel={handleClose}
        footer={null}
        destroyOnClose
        maskClosable={!uploading}
        closable={!uploading}
      >
        {errorMsg && (
          <Alert
            message={errorMsg}
            type="error"
            showIcon
            closable
            onClose={() => setErrorMsg('')}
            style={{ marginBottom: '16px' }}
          />
        )}

        {uploading && (
          <div style={{ textAlign: 'center', margin: '20px 0' }}>
            <Spin tip={i18n('sl_processing_video')} size="large" />
          </div>
        )}

        <div style={{ display: uploading ? 'none' : 'block' }}>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key)
              setErrorMsg('')
            }}
            items={tabItems}
          />
        </div>
      </Modal>
    </>
  )
}

export default UploadSliderVideo
